import { useMemo, useState } from 'react';
import {
  Icon,
  InlineStack,
  Panel,
  PanelHeader,
  PanelInset,
  PanelTitle,
  TableTd,
  TableTr,
} from '@customerio/pluma-components/react';
import { useActivityLogEnvironment } from '@/services/db/activity-log/queries';
import type { ActivityLogRow, ActivityLogSegment } from '@/services/db/activity-log/entity';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { NameCell } from '@/pages/workspaces/activity-logs/components/cells';
import { TimestampCell } from '@/pages/workspaces/activity-logs/components/columns';
import { ExpandedRow } from '@/pages/workspaces/activity-logs/components/expanded-row';
import type { IdentifierConfig } from '@/pages/workspaces/activity-logs/display-name';
import { ActivityLogTable, useObjectActivityLogs, type ActivityLogTableColumn } from './activity-log-table';
import { useObjectDetail } from './context';

const COLUMNS: readonly ActivityLogTableColumn[] = [
  { label: 'Activity name' },
  { label: 'Timestamp', textAlign: 'right' },
];

/** The log type that records an attribute write against an object of this type. */
export function attributeChangeLogType(objectTypeId: string): string {
  return `_o:${objectTypeId}:attribute_change`;
}

type AttributeChangeRowProps = {
  row: ActivityLogRow;
  workspaceId: number;
  campaignSingular: string;
  identifiers: IdentifierConfig;
  segmentById: Map<number, ActivityLogSegment>;
  onResent: () => void;
};

function AttributeChangeRow({ row, workspaceId, ...props }: AttributeChangeRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <TableTr
        role="button"
        cursor="pointer"
        backgroundColor={isExpanded ? 'subtle' : undefined}
        onClick={() => setIsExpanded(!isExpanded)}
        data-test-activity-log-row-main
      >
        <TableTd>
          <NameCell row={row} workspaceId={String(workspaceId)} identifiers={props.identifiers} />
        </TableTd>
        <TableTd textAlign="right">
          <InlineStack alignInline="flex-end" alignBlock="center" gap="100">
            <TimestampCell value={row.log.timestamp} />
            <Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} flexShrink="0" />
          </InlineStack>
        </TableTd>
      </TableTr>
      {isExpanded ? (
        <TableTr data-test-activity-log-row-json>
          <TableTd colSpan={COLUMNS.length} p="0">
            <ExpandedRow
              row={row}
              workspaceId={String(workspaceId)}
              campaignSingular={props.campaignSingular}
              isAnonymous={false}
              identifiers={props.identifiers}
              segmentById={props.segmentById}
              onResent={props.onResent}
            />
          </TableTd>
        </TableTr>
      ) : null}
    </>
  );
}

export function ObjectAttributeChanges() {
  const { record, objectType, workspaceId } = useObjectDetail();
  const taxonomy = useProductTaxonomy();
  const { identifiers } = useActivityLogEnvironment(String(workspaceId));
  const { query, rows, segmentById } = useObjectActivityLogs(workspaceId, record.identifiers.cio_object_id, {
    type: attributeChangeLogType(objectType.id),
  });
  const campaignSingular = useMemo(() => taxonomy.term('campaigns.titleSingular'), [taxonomy]);

  return (
    <Panel data-test-object-attribute-changes>
      <PanelHeader aria-labelledby="attribute-changes-header">
        <PanelTitle id="attribute-changes-header" size="md">
          Recent Attribute Changes
        </PanelTitle>
      </PanelHeader>
      <PanelInset>
        <ActivityLogTable
          caption="Recent Attribute Changes"
          columns={COLUMNS}
          query={query}
          errorMessage="Could not load recent attribute changes."
          rows={rows}
          shouldHighlightOnHover
          renderRow={(row) => (
            <AttributeChangeRow
              key={row.log.id}
              row={row}
              workspaceId={workspaceId}
              campaignSingular={campaignSingular}
              identifiers={identifiers}
              segmentById={segmentById}
              onResent={() => void query.refetch()}
            />
          )}
        />
      </PanelInset>
    </Panel>
  );
}
